
import React, { useEffect, lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import Layout from '@/components/Layout/Layout';
import HeroSection from '@/components/Home/HeroSection';
import IntroductionSection from '@/components/Home/IntroductionSection';
import { preloadCriticalResources } from '@/utils/seoEnhancements';
import { preloadCriticalImage, optimizeImageUrl } from '@/utils/imageOptimization';

// Lazy load non-critical components
const HomeServicesSection = lazy(() => import('@/components/Home/HomeServicesSection'));
const ImageGallery = lazy(() => import('@/components/Home/ImageGallery'));
const FeaturedPages = lazy(() => import('@/components/Home/FeaturedPages'));
const CTASection = lazy(() => import('@/components/Home/CTASection'));

const HomePage = () => {
  useEffect(() => {
    // Preload critical resources for performance
    preloadCriticalResources();
    
    // Preload hero image for LCP optimization
    preloadCriticalImage(optimizeImageUrl('https://images.unsplash.com/photo-1632759145351-1d592919f522', {
      width: 1920,
      height: 1080,
      quality: 75
    }));
  }, []);

  return (
    <Layout>
      <Helmet>
        <title>Carolina Trophy Roofs | #1 Roofing Contractor in Rocky Mount, NC</title>
        <meta name="description" content="Carolina Trophy Roofs is the top-rated roofing contractor serving Rocky Mount, NC and Eastern North Carolina. Expert residential & commercial roofing services, storm damage restoration, and insurance claims assistance. Call (252) 302-2003 for free estimates." />
        <meta name="keywords" content="roofing contractor, Rocky Mount NC, Greenville NC, roof replacement, roof repair, storm damage, insurance claims, commercial roofing, residential roofing, Eastern NC" />
        <link rel="canonical" href="https://carolinatrophyroofs.com/" />
        
        {/* Enhanced Open Graph */}
        <meta property="og:title" content="Carolina Trophy Roofs | #1 Roofing Contractor in Rocky Mount, NC" />
        <meta property="og:description" content="Top-rated roofing contractor in Eastern NC. Expert roof replacement, repair, and storm damage restoration. Free estimates and insurance assistance." />
        <meta property="og:image" content="https://carolinatrophyroofs.com/lovable-uploads/f93ed555-e5de-49b3-9fde-50a146ff31c8.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:url" content="https://carolinatrophyroofs.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:site_name" content="Carolina Trophy Roofs" />
        
        {/* Twitter Enhanced */}
        <meta name="twitter:title" content="Carolina Trophy Roofs | #1 Roofing Contractor in Rocky Mount, NC" />
        <meta name="twitter:description" content="Top-rated roofing contractor in Eastern NC. Expert roof replacement, repair, and storm damage restoration." />
        
        {/* Local Business Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RoofingContractor",
            "@id": "https://carolinatrophyroofs.com/#organization",
            "name": "Carolina Trophy Roofs",
            "url": "https://carolinatrophyroofs.com",
            "logo": "https://carolinatrophyroofs.com/lovable-uploads/2f5a66d3-a9ff-41b4-888b-9060533e361f.png",
            "description": "Eastern North Carolina's premier roofing contractor specializing in residential and commercial roofing services, storm damage restoration, and insurance claims assistance.",
            "telephone": "+1-252-302-2003",
            "email": "Claims@CarolinaTrophyRoofs.com",
            "foundingDate": "2010",
            "address": [
              {
                "@type": "PostalAddress",
                "streetAddress": "104 S. Business Ct",
                "addressLocality": "Rocky Mount",
                "addressRegion": "NC",
                "postalCode": "27804",
                "addressCountry": "US"
              },
              {
                "@type": "PostalAddress", 
                "streetAddress": "1234 Evans St",
                "addressLocality": "Greenville",
                "addressRegion": "NC", 
                "postalCode": "27834",
                "addressCountry": "US"
              }
            ],
            "geo": [
              {
                "@type": "GeoCoordinates",
                "latitude": 35.9382,
                "longitude": -77.7905
              },
              {
                "@type": "GeoCoordinates", 
                "latitude": 35.6127,
                "longitude": -77.3663
              }
            ],
            "areaServed": [
              { "@type": "City", "name": "Rocky Mount", "containedInPlace": { "@type": "State", "name": "North Carolina" }},
              { "@type": "City", "name": "Greenville", "containedInPlace": { "@type": "State", "name": "North Carolina" }},
              { "@type": "City", "name": "Wilson", "containedInPlace": { "@type": "State", "name": "North Carolina" }},
              { "@type": "City", "name": "Zebulon", "containedInPlace": { "@type": "State", "name": "North Carolina" }},
              { "@type": "City", "name": "Tarboro", "containedInPlace": { "@type": "State", "name": "North Carolina" }}
            ],
            "serviceType": ["Roof Replacement", "Roof Repair", "Storm Damage Restoration", "Commercial Roofing", "Residential Roofing", "Insurance Claims"],
            "openingHoursSpecification": [
              {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                "opens": "07:00",
                "closes": "19:00"
              },
              {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": "Saturday", 
                "opens": "08:00",
                "closes": "17:00"
              }
            ],
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Roofing Services",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Emergency Roof Repair",
                    "description": "24/7 emergency roofing services for storm damage and urgent repairs"
                  }
                },
                {
                  "@type": "Offer", 
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Complete Roof Replacement",
                    "description": "Full roof replacement with premium materials and expert installation"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Commercial Roofing",
                    "description": "Professional commercial roofing services for businesses and industrial buildings"
                  }
                }
              ]
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "reviewCount": "247",
              "bestRating": "5",
              "worstRating": "1"
            },
            "contactPoint": [
              {
                "@type": "ContactPoint",
                "telephone": "+1-252-302-2003",
                "contactType": "customer service", 
                "areaServed": "NC",
                "availableLanguage": "English",
                "contactOption": "TollFree"
              }
            ],
            "sameAs": [
              "https://www.facebook.com/carolinatrophyroofs",
              "https://www.instagram.com/carolinatrophyroofs/",
              "https://x.com/RoofsTrophy",
              "https://www.linkedin.com/company/81146474/",
              "https://www.youtube.com/channel/UC8pyyQN0WcMTbT-OHTQbhkA"
            ]
          })}
        </script>
      </Helmet>
      <HeroSection />
      <IntroductionSection />
      
      {/* SkyQuote Embed Section - moved here */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-brand-charcoal-gray mb-4">
              Get Your Free INSTANT Estimate!
            </h2>
            <p className="text-lg text-gray-600">
              Find your address below and get a free INSTANT estimate.
            </p>
          </div>
          <div 
            className="w-full min-h-[400px] bg-white rounded-lg shadow-lg p-4"
          >
            <iframe 
              src="https://sky-quote.com/instaquote.html?publicKey=67c78ce800214e84a69b&SkyQuoteUrl=https%3A%2F%2Fsky-quote.com"
              width="100%" 
              height="500"
              style={{border: 'none', borderRadius: '8px'}}
              allow="geolocation; fullscreen"
              title="SkyQuote Estimate Form"
            />
          </div>
        </div>
      </section>
      
      <Suspense fallback={<div className="flex justify-center items-center py-16"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-trophy-gold"></div></div>}>
        <HomeServicesSection />
        <ImageGallery />
        <FeaturedPages />
        <CTASection />
      </Suspense>
    </Layout>
  );
};

export default HomePage;
